import { useEffect, useState } from "react";
import api, { apiError, API, getToken } from "@/lib/api";
import { rupee } from "@/lib/constants";
import { toast } from "sonner";
import { useLiveRefresh } from "@/lib/useLiveRefresh";
import { Loader2, HeartHandshake, CalendarClock, User, Phone, Check, X, UserCheck, Download, MapPin } from "lucide-react";
import { Timeline } from "@/components/Timeline";
import { FilterBar, inRange, uniq } from "@/components/admin/FilterBar";

const STATUSES = ["REQUESTED", "CONFIRMED", "ON_THE_WAY", "ARRIVED", "IN_PROGRESS", "COMPLETED", "CANCELLED"];
const ACTIVE = ["REQUESTED", "CONFIRMED", "ON_THE_WAY", "ARRIVED", "IN_PROGRESS"];
const PROGRESS = { CONFIRMED: ["ON_THE_WAY", "Caregiver on the way"], ON_THE_WAY: ["ARRIVED", "Caregiver arrived"], ARRIVED: ["IN_PROGRESS", "Start session"], IN_PROGRESS: ["COMPLETED", "Mark completed"] };
const SC = {
    REQUESTED: "bg-amber-50 text-amber-700 border-amber-200",
    CONFIRMED: "bg-emerald-50 text-emerald-700 border-emerald-200",
    ON_THE_WAY: "bg-blue-50 text-blue-700 border-blue-200",
    ARRIVED: "bg-indigo-50 text-indigo-700 border-indigo-200",
    IN_PROGRESS: "bg-violet-50 text-violet-700 border-violet-200",
    COMPLETED: "bg-teal-50 text-teal-700 border-teal-200",
    CANCELLED: "bg-red-50 text-red-700 border-red-200",
};
const WINDOWS = ["9am – 12pm", "12pm – 3pm", "3pm – 6pm", "6pm – 8pm"];

export default function AdminCareVisits() {
    const [data, setData] = useState(null);
    const [filter, setFilter] = useState("ALL");
    const [q, setQ] = useState("");
    const [planF, setPlanF] = useState("");
    const [dates, setDates] = useState({ from: "", to: "" });

    const load = () => api.get("/admin/care-visits").then((r) => setData(r.data)).catch(() => setData(false));
    useEffect(() => { load(); }, []);
    useLiveRefresh(load, 15000);

    if (!data) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-orange" /></div>;

    const visits = data.visits.filter((v) => (filter === "ALL" || v.status === filter) && (!planF || v.plan_name === planF)
        && inRange(v.scheduled_date || v.date || v.created_at, dates) && (!q || JSON.stringify(v).toLowerCase().includes(q.toLowerCase())));

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2"><HeartHandshake size={22} className="text-[#F43F5E]" /><h1 className="font-heading text-2xl font-extrabold text-navy">Care Visits</h1></div>
                <a data-testid="care-visits-export-btn" href={`${API}/admin/care-visits-export.csv?auth=${encodeURIComponent(getToken() || "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 rounded-full bg-navy px-4 py-2 text-sm font-bold text-white"><Download size={15} /> Export (CSV)</a>
            </div>
            <p className="text-sm text-navy/55">Confirm, schedule and assign verified caregivers to HOMIIE Care requests</p>

            <div className="mt-4 flex flex-wrap gap-2">
                <Chip label={`All (${data.visits.length})`} active={filter === "ALL"} onClick={() => setFilter("ALL")} />
                {STATUSES.map((s) => <Chip key={s} testid={`care-filter-${s}`} label={`${s} (${data.counts[s] || 0})`} active={filter === s} onClick={() => setFilter(s)} />)}
            </div>
            <FilterBar testid="care-visit-filter" q={q} setQ={setQ} placeholder="Search care ID, customer, caregiver, phone…" dates={dates} setDates={setDates}
                selects={[{ key: "plan", label: "All plans", value: planF, set: setPlanF, options: uniq(data.visits.map((v) => v.plan_name)) }]}
                onClear={() => { setQ(""); setPlanF(""); setDates({ from: "", to: "" }); setFilter("ALL"); }} />

            <div className="mt-5 space-y-4">
                {visits.length === 0 && <div className="rounded-2xl border border-dashed border-navy/15 py-12 text-center text-sm text-navy/40">No care visits here yet.</div>}
                {visits.map((v) => <VisitCard key={v.id} v={v} caregivers={data.caregivers} onSaved={load} />)}
            </div>
        </div>
    );
}

function Chip({ label, active, onClick, testid }) {
    return <button data-testid={testid} onClick={onClick} className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${active ? "bg-navy text-white" : "border border-navy/15 bg-white text-navy/60"}`}>{label}</button>;
}

function VisitCard({ v, caregivers, onSaved }) {
    const [caregiver, setCaregiver] = useState(v.caregiver_id || "");
    const [date, setDate] = useState(v.scheduled_date || v.date || "");
    const [slot, setSlot] = useState(v.scheduled_window || v.window || "");
    const [note, setNote] = useState(v.admin_note || "");
    const [skillFilter, setSkillFilter] = useState("");
    const [busy, setBusy] = useState(false);

    const allSkills = [...new Set(caregivers.flatMap((c) => c.skills || []))].sort();
    const shown = skillFilter ? caregivers.filter((c) => (c.skills || []).includes(skillFilter)) : caregivers;
    const cov = v.plan_coverage;

    const patch = async (body, msg) => {
        setBusy(true);
        try { await api.patch(`/admin/care-visits/${v.id}`, body); toast.success(msg); onSaved(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const confirm = () => {
        if (!caregiver) return toast.error("Assign a verified caregiver first");
        if (!date || !slot) return toast.error("Set a scheduled date and time");
        patch({ status: "CONFIRMED", caregiver_id: caregiver, scheduled_date: date, scheduled_window: slot, admin_note: note }, "Visit confirmed & customer notified");
    };

    return (
        <div data-testid={`care-visit-${v.id}`} className="rounded-2xl border border-navy/10 bg-white p-5">
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                    <div className="flex items-center gap-2">
                        <h3 className="font-heading text-base font-extrabold text-navy">{v.plan_name}</h3>
                        <span className="font-mono text-xs text-navy/40">{v.care_id}</span>
                    </div>
                    <p className="mt-0.5 text-sm text-navy/60">{cov ? "Covered by plan" : rupee(v.price)} · for {v.member_name}</p>
                    {cov && <span data-testid={`care-coverage-${v.id}`} className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-[#F43F5E]/10 px-2.5 py-1 text-[11px] font-bold text-[#F43F5E]">{cov.sub_id} · {cov.remaining}/{cov.visits_total} visits left</span>}
                </div>
                <span className={`rounded-full border px-2.5 py-1 text-xs font-bold ${SC[v.status]}`} data-testid={`care-status-${v.id}`}>{v.status}</span>
            </div>

            <div className="mt-3 grid grid-cols-1 gap-2 text-sm text-navy/70 sm:grid-cols-2">
                <span className="flex items-center gap-1.5"><User size={14} className="text-navy/40" /> {v.customer_name || "—"}</span>
                <span className="flex items-center gap-1.5"><Phone size={14} className="text-navy/40" /> {v.customer_phone || "—"}</span>
                <span className="flex items-center gap-1.5"><CalendarClock size={14} className="text-navy/40" /> Requested: {v.date} · {v.window}</span>
                {v.caregiver_name && <span className="flex items-center gap-1.5"><UserCheck size={14} className="text-emerald-500" /> {v.caregiver_name}</span>}
                {(v.address || v.pincode) && <span className="flex items-center gap-1.5"><MapPin size={14} className="text-navy/40" /> {v.address || ""} {v.pincode || ""}</span>}
            </div>
            {v.notes && <p className="mt-2 rounded-lg bg-navy/[0.03] p-2 text-xs text-navy/60">Customer note: {v.notes}</p>}

            {ACTIVE.includes(v.status) && (
                <div className="mt-4 space-y-3 border-t border-navy/10 pt-4">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <div>
                            <label className="mb-1 block text-xs font-bold text-navy/60">Caregiver</label>
                            {allSkills.length > 0 && (
                                <select data-testid={`care-skill-filter-${v.id}`} value={skillFilter} onChange={(e) => setSkillFilter(e.target.value)} className="mb-1.5 w-full rounded-xl border border-navy/10 bg-navy/[0.02] px-3 py-1.5 text-xs outline-none focus:border-[#F43F5E]">
                                    <option value="">Filter by skill — all</option>
                                    {allSkills.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            )}
                            <select data-testid={`care-caregiver-${v.id}`} value={caregiver} onChange={(e) => setCaregiver(e.target.value)} className="w-full rounded-xl border border-navy/15 px-3 py-2 text-sm outline-none focus:border-[#F43F5E]">
                                <option value="">Select caregiver…</option>
                                {shown.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.pro_id}){c.skills?.length ? ` · ${c.skills.join(", ")}` : ""}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="mb-1 block text-xs font-bold text-navy/60">Scheduled date</label>
                            <input data-testid={`care-date-${v.id}`} type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full rounded-xl border border-navy/15 px-3 py-2 text-sm outline-none focus:border-[#F43F5E]" />
                        </div>
                        <div>
                            <label className="mb-1 block text-xs font-bold text-navy/60">Time window</label>
                            <select data-testid={`care-window-${v.id}`} value={slot} onChange={(e) => setSlot(e.target.value)} className="w-full rounded-xl border border-navy/15 px-3 py-2 text-sm outline-none focus:border-[#F43F5E]">
                                <option value="">Select…</option>
                                {WINDOWS.map((w) => <option key={w} value={w}>{w}</option>)}
                            </select>
                        </div>
                    </div>
                    <input data-testid={`care-note-${v.id}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Internal note (optional)" className="w-full rounded-xl border border-navy/15 px-3 py-2 text-sm outline-none focus:border-[#F43F5E]" />
                    <div className="flex flex-wrap gap-2">
                        {v.status === "REQUESTED" && <button data-testid={`care-confirm-${v.id}`} disabled={busy} onClick={confirm} className="flex items-center gap-1.5 rounded-full bg-emerald-500 px-4 py-2 text-sm font-bold text-white disabled:opacity-60"><Check size={15} /> Confirm & assign</button>}
                        {v.status !== "REQUESTED" && <>
                            <button data-testid={`care-reschedule-${v.id}`} disabled={busy} onClick={() => patch({ caregiver_id: caregiver, scheduled_date: date, scheduled_window: slot, admin_note: note }, "Updated")} className="rounded-full bg-navy px-4 py-2 text-sm font-bold text-white disabled:opacity-60">Save changes</button>
                            {PROGRESS[v.status] && <button data-testid={`care-progress-${v.id}`} disabled={busy} onClick={() => patch({ status: PROGRESS[v.status][0] }, PROGRESS[v.status][1])} className="rounded-full bg-teal px-4 py-2 text-sm font-bold text-white disabled:opacity-60">{PROGRESS[v.status][1]}</button>}
                            {v.status !== "IN_PROGRESS" && <button data-testid={`care-complete-${v.id}`} disabled={busy} onClick={() => patch({ status: "COMPLETED" }, "Marked completed")} className="rounded-full border border-teal px-4 py-2 text-sm font-bold text-teal disabled:opacity-60">Mark completed</button>}
                        </>}
                        <button data-testid={`care-cancel-${v.id}`} disabled={busy} onClick={() => patch({ status: "CANCELLED" }, "Cancelled")} className="flex items-center gap-1.5 rounded-full border border-red-300 px-4 py-2 text-sm font-bold text-red-600 hover:bg-red-50 disabled:opacity-60"><X size={15} /> Cancel</button>
                    </div>
                </div>
            )}
            <details className="mt-3 rounded-xl bg-navy/[0.02] p-3" data-testid={`care-visit-timeline-${v.id}`}>
                <summary className="cursor-pointer text-xs font-bold text-navy/60">Timeline ({(v.timeline || []).length || 1} events)</summary>
                <div className="mt-2"><Timeline items={v.timeline || [{ label: "Visit requested", at: v.created_at }]} accent="#F43F5E" testid={`care-tl-${v.id}`} /></div>
            </details>
        </div>
    );
}
